import { useAuthStore } from '@/stores/auth.store';

const WS_URL = import.meta.env.VITE_WS_URL || `ws://${window.location.host}/api/ws/chat`;

export type WSEventType =
  | 'auth:success'
  | 'subscribed'
  | 'unsubscribed'
  | 'message:new'
  | 'message:updated'
  | 'message:removed'
  | 'message:system'
  | 'message:sent'
  | 'messages:read'
  | 'reaction:added'
  | 'reaction:removed'
  | 'typing:start'
  | 'typing:stop'
  | 'presence:update'
  | 'participant:online'
  | 'participant:offline'
  | 'unread:sync'
  | 'heartbeat:ack'
  | 'error'
  | 'connect'
  | 'disconnect';

interface WSMessage {
  type: WSEventType;
  payload?: unknown;
}

type MessageHandler = (payload: unknown) => void;

class WebSocketClient {
  private socket: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private heartbeatInterval: number | null = null;
  private handlers: Map<WSEventType, Set<MessageHandler>> = new Map();
  private pendingSubscriptions: Set<string> = new Set();
  private isAuthenticated = false;

  connect(): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      console.log('[WS] Already connected, skipping');
      return;
    }

    const token = useAuthStore.getState().accessToken;
    if (!token) {
      console.warn('[WS] ❌ Cannot connect: no auth token');
      return;
    }

    console.log(`[WS] 🔌 Connecting to ${WS_URL} …`);
    try {
      this.socket = new WebSocket(WS_URL);
      this.setupEventHandlers();
    } catch (error) {
      console.error('[WS] ❌ Connection error:', error);
      this.handleReconnect();
    }
  }

  private setupEventHandlers(): void {
    if (!this.socket) return;

    this.socket.onopen = () => {
      console.log('[WS] ✅ TCP connected — sending auth token …');
      this.reconnectAttempts = 0;
      this.authenticate();
      this.emit('connect', {});
    };

    this.socket.onmessage = (event) => {
      try {
        const message: WSMessage = JSON.parse(event.data);
        this.handleMessage(message);
      } catch (error) {
        console.error('[WS] ❌ Failed to parse incoming message:', error, event.data);
      }
    };

    this.socket.onclose = (event) => {
      const cleanLabel = event.wasClean ? 'clean' : 'unclean';
      console.warn(`[WS] 🔴 Disconnected (${cleanLabel}) — code ${event.code}${event.reason ? ` reason: ${event.reason}` : ''}`);
      this.cleanup();
      this.emit('disconnect', { code: event.code, reason: event.reason });
      if (!event.wasClean) {
        this.handleReconnect();
      }
    };

    this.socket.onerror = (error) => {
      // error event has no useful fields in browsers — actual details appear in onclose
      console.error('[WS] ❌ Socket error (check server is running on port 5002):', error);
    };
  }

  private authenticate(): void {
    const token = useAuthStore.getState().accessToken;
    if (token) {
      console.log('[WS] 🔑 Sending auth frame …');
      this.send('auth', { token });
    } else {
      console.error('[WS] ❌ No token available during auth step');
    }
  }

  private handleMessage(message: WSMessage): void {
    const { type, payload } = message;

    if (type === 'auth:success') {
      this.isAuthenticated = true;
      this.startHeartbeat();
      console.log('[WS] ✅ Authenticated!', payload);

      if (this.pendingSubscriptions.size > 0) {
        console.log('[WS] 🔁 Re-subscribing to pending conversations:', [...this.pendingSubscriptions]);
        this.pendingSubscriptions.forEach(conversationId => {
          this.subscribe(conversationId);
        });
        this.pendingSubscriptions.clear();
      }
    } else if (type === 'error') {
      console.error('[WS] ❌ Server error:', payload);
    } else if (type === 'message:new') {
      console.log('[WS] 📨 New message received:', payload);
    } else if (type === 'subscribed') {
      console.log('[WS] 📬 Subscribed to conversation:', (payload as any)?.conversationId);
    } else if (type === 'heartbeat:ack') {
      // silence heartbeat spam — uncomment below to debug heartbeats
      // console.log('[WS] 💓 Heartbeat ack');
    } else if (type === 'presence:update') {
      console.log('[WS] 👤 Presence update:', payload);
    } else if (type === 'typing:start' || type === 'typing:stop') {
      console.log(`[WS] ✏️ ${type}:`, payload);
    } else {
      console.log(`[WS] 📩 Event [${type}]:`, payload);
    }

    // Notify handlers
    this.emit(type, payload);
  }

  private emit(event: WSEventType, payload: unknown): void {
    const handlers = this.handlers.get(event);
    if (handlers) {
      handlers.forEach(handler => {
        try {
          handler(payload);
        } catch (error) {
          console.error(`Handler error for ${event}:`, error);
        }
      });
    }
  }

  private handleReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error(`[WS] ❌ Max reconnection attempts (${this.maxReconnectAttempts}) reached — giving up`);
      return;
    }

    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts);
    this.reconnectAttempts++;

    console.warn(`[WS] 🔄 Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts}) …`);

    setTimeout(() => {
      this.connect();
    }, delay);
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatInterval = window.setInterval(() => {
      this.send('heartbeat', {});
    }, 30000);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  private cleanup(): void {
    this.stopHeartbeat();
    this.isAuthenticated = false;
  }

  // Public methods

  send(type: string, payload: Record<string, unknown>): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify({ type, payload }));
    } else {
      console.warn('WebSocket not connected, cannot send message');
    }
  }

  subscribe(conversationId: string): void {
    if (!this.isAuthenticated) {
      console.log(`[WS] ⏳ Not yet authenticated — queuing subscription for ${conversationId}`);
      this.pendingSubscriptions.add(conversationId);
      return;
    }
    console.log(`[WS] 📡 Subscribing to conversation ${conversationId}`);
    this.send('subscribe', { conversationId });
  }

  unsubscribe(conversationId: string): void {
    this.pendingSubscriptions.delete(conversationId);
    if (this.isAuthenticated) {
      this.send('unsubscribe', { conversationId });
    }
  }

  sendMessage(conversationId: string, content: string, options?: {
    attachments?: unknown[];
    replyTo?: string;
    mentions?: unknown[];
  }): void {
    this.send('message:send', {
      conversationId,
      content,
      ...options,
    });
  }

  editMessage(messageId: string, content: string): void {
    this.send('message:edit', { messageId, content });
  }

  deleteMessage(messageId: string): void {
    this.send('message:delete', { messageId });
  }

  markAsRead(conversationId: string, upToMessageId?: string): void {
    this.send('message:read', { conversationId, upToMessageId });
  }

  addReaction(messageId: string, emoji: string): void {
    this.send('reaction:add', { messageId, emoji });
  }

  removeReaction(messageId: string, emoji: string): void {
    this.send('reaction:remove', { messageId, emoji });
  }

  startTyping(conversationId: string): void {
    this.send('typing:start', { conversationId });
  }

  stopTyping(conversationId: string): void {
    this.send('typing:stop', { conversationId });
  }

  updatePresence(status: 'online' | 'away' | 'busy'): void {
    this.send('presence:update', { status });
  }

  on(event: WSEventType, handler: MessageHandler): () => void {
    if (!this.handlers.has(event)) {
      this.handlers.set(event, new Set());
    }
    this.handlers.get(event)!.add(handler);

    // Return unsubscribe function
    return () => {
      this.handlers.get(event)?.delete(handler);
    };
  }

  off(event: WSEventType, handler: MessageHandler): void {
    this.handlers.get(event)?.delete(handler);
  }

  disconnect(): void {
    this.cleanup();
    if (this.socket) {
      this.socket.close(1000, 'Client disconnect');
      this.socket = null;
    }
  }

  get isConnected(): boolean {
    return this.socket?.readyState === WebSocket.OPEN && this.isAuthenticated;
  }
}

// Singleton instance
export const wsClient = new WebSocketClient();

// Connect immediately if already logged in (e.g. page refresh)
const _initialToken = useAuthStore.getState().accessToken;
if (_initialToken) {
  wsClient.connect();
}

// Auto-connect when auth changes
useAuthStore.subscribe((state, prevState) => {
  if (state.accessToken && !prevState.accessToken) {
    wsClient.connect();
  } else if (!state.accessToken && prevState.accessToken) {
    wsClient.disconnect();
  }
});
